import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Book, Calendar, BarChart3, Users, DollarSign, Eye, EyeOff, Globe, Link as LinkIcon, Clock, Percent, UserPlus, Copy, ChevronDown, ChevronUp, ExternalLink, User } from 'lucide-react';
import { AuthorWorksCarousel } from '../components/books/AuthorWorksCarousel';
import { books, getBookById } from '../data/books';
import { currentUser } from '../data/currentUser';
export function BookDetailPage() {
  const {
    bookId
  } = useParams();
  const navigate = useNavigate();
  const [previewExpanded, setPreviewExpanded] = useState(false);
  // Find the book with the matching ID from centralized data
  const book = getBookById(bookId || '');
  // Check if the current user is the author of the book
  const isCurrentUserAuthor = book && currentUser && book.author.id === currentUser.id;
  // State for preview settings
  const [previewSettings, setPreviewSettings] = useState(book?.preview || {
    enabled: false,
    percentage: 10,
    whitelist: [],
    timeWindow: {
      enabled: false,
      startDate: '',
      endDate: ''
    }
  });
  // State for new email to add to whitelist
  const [newEmail, setNewEmail] = useState('');
  // If book not found, show error
  if (!book) {
    return <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-300">
        <div className="text-center" data-id="element-301">
          <h1 className="text-2xl font-bold text-[#164149] mb-2" data-id="element-302">
            Work not found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-303">
            The work you are looking for does not exist or has been removed.
          </p>
          <button onClick={() => navigate('/my-works')} className="px-4 py-2 bg-[#161c2d] text-white rounded-md hover:bg-[#1e2740] transition-colors" data-id="element-304">
            Return to My Works
          </button>
        </div>
      </div>;
  }
  // Toggle preview enabled state
  const togglePreviewEnabled = () => {
    setPreviewSettings({
      ...previewSettings,
      enabled: !previewSettings.enabled
    });
  };
  // Toggle time window enabled state
  const toggleTimeWindowEnabled = () => {
    setPreviewSettings({
      ...previewSettings,
      timeWindow: {
        ...previewSettings.timeWindow,
        enabled: !previewSettings.timeWindow.enabled
      }
    });
  };
  // Add email to whitelist
  const addEmailToWhitelist = () => {
    if (newEmail && !previewSettings.whitelist.includes(newEmail)) {
      setPreviewSettings({
        ...previewSettings,
        whitelist: [...previewSettings.whitelist, newEmail]
      });
      setNewEmail('');
    }
  };
  // Remove email from whitelist
  const removeEmailFromWhitelist = (email: string) => {
    setPreviewSettings({
      ...previewSettings,
      whitelist: previewSettings.whitelist.filter(e => e !== email)
    });
  };
  // Handle percentage change
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setPreviewSettings({
      ...previewSettings,
      percentage: value
    });
  };
  // Handle date changes
  const handleDateChange = (field: string, value: string) => {
    setPreviewSettings({
      ...previewSettings,
      timeWindow: {
        ...previewSettings.timeWindow,
        [field]: value
      }
    });
  };
  // Copy public link to clipboard
  const copyPublicLink = () => {
    const url = `${window.location.origin}/book/public/${bookId}`;
    navigator.clipboard.writeText(url);
    // In a real app, you would show a toast notification here
    alert('Link copied to clipboard!');
  };
  // Navigate to public book page - standardized navigation
  const goToPublicPage = () => {
    navigate(`/book/public/${bookId}`);
  };
  return <div className="w-full min-h-screen bg-gray-50 -m-3 sm:-m-4 md:-m-6" data-id="element-305">
      {/* Hero Section with Blurred Background - removed margins to make it flush with sidebar and navbar */}
      <div className="w-[calc(100%+1.5rem)] sm:w-[calc(100%+2rem)] md:w-[calc(100%+3rem)] h-[280px] relative overflow-hidden -mr-3 sm:-mr-4 md:-mr-6" style={{
      backgroundImage: `url(${book.cover})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} data-id="element-306">
        {/* Blur overlay - ensuring it extends to all edges */}
        <div className="absolute inset-0 right-0 left-0 backdrop-blur-xl bg-black/30" data-id="element-307"></div>
        {/* Back button */}
        <div className="absolute top-4 left-4 z-10" data-id="element-308">
          <button onClick={() => navigate('/my-works')} className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm rounded-md text-white hover:bg-white/30 transition-colors" data-id="element-309">
            <ArrowLeft size={16} className="mr-2" data-id="element-310" />
            Back to My Works
          </button>
        </div>
      </div>
      {/* Book Details Section - removed max-width constraint to match header width */}
      <div className="w-full px-4 sm:px-6 -mt-32 relative z-10" data-id="element-311">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-id="element-312">
          {/* Book Cover */}
          <div className="flex-shrink-0 w-[200px] md:w-[240px] mx-auto md:mx-0" data-id="element-313">
            <div className="aspect-[1/1.5] shadow-xl rounded-lg overflow-hidden" data-id="element-314">
              <img src={book.cover} alt={book.title} className="w-full h-full object-cover" data-id="element-315" />
            </div>
          </div>
          {/* Book Info */}
          <div className="flex-1 bg-white rounded-lg shadow-md p-6 md:p-8" data-id="element-316">
            <h1 className="text-2xl md:text-3xl font-bold text-[#164149] mb-4" data-id="element-317">
              {book.title}
            </h1>
            {/* Publishing Details - moved up from below */}
            <div className="mb-6" data-id="element-318">
              <h2 className="text-lg font-semibold text-[#164149] mb-3" data-id="element-319">
                Publishing Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-320">
                <div data-id="element-321">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-322">
                    Publisher
                  </h3>
                  <p className="text-gray-900" data-id="element-323">{book.publisher}</p>
                </div>
                <div data-id="element-324">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-325">
                    Publication Year
                  </h3>
                  <p className="text-gray-900" data-id="element-326">{book.year}</p>
                </div>
                <div data-id="element-327">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-328">
                    Genre
                  </h3>
                  <p className="text-gray-900" data-id="element-329">{book.genre}</p>
                </div>
                <div data-id="element-330">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-331">
                    Number of Pages
                  </h3>
                  <p className="text-gray-900" data-id="element-332">{book.pages}</p>
                </div>
                <div data-id="element-333">
                  <h3 className="text-sm font-medium text-gray-500 mb-1" data-id="element-334">
                    Status
                  </h3>
                  <p className="text-gray-900" data-id="element-335">{getStatusLabel(book.status)}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-end" data-id="element-336">
              {/* Only show "Edit Work" if the current user is the author */}
              {isCurrentUserAuthor && <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-id="element-337">
                  Edit Work
                </button>}
              <button onClick={goToPublicPage} className="px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors flex items-center" data-id="element-338">
                <Globe size={16} className="mr-2" data-id="element-339" />
                Public Page
                <ExternalLink size={14} className="ml-2" data-id="element-340" />
              </button>
            </div>
          </div>
        </div>
        {/* Description */}
        <div className="mt-8" data-id="element-341">
          <div className="bg-white rounded-lg border border-gray-200/70" data-id="element-342">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100" data-id="element-343">
              <div className="flex items-center" data-id="element-344">
                <h2 className="text-base font-semibold text-[#164149]" data-id="element-345">
                  Description
                </h2>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4" data-id="element-346">
              <div className="p-4" data-id="element-347">
                <p className="text-gray-700" data-id="element-348">{book.description}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Preview Management Section - only visible if the current user is the author */}
        {isCurrentUserAuthor && <div className="mt-8" data-id="element-349">
            <div className="bg-white rounded-lg border border-gray-200/70 overflow-hidden" data-id="element-350">
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 cursor-pointer" onClick={() => setPreviewExpanded(!previewExpanded)} data-id="element-351">
                <div className="flex items-center" data-id="element-352">
                  <Eye size={18} className="text-[#376770] mr-2" data-id="element-353" />
                  <h2 className="text-base font-semibold text-[#376770]" data-id="element-354">
                    Preview Management
                  </h2>
                </div>
                <button className="text-gray-500" data-id="element-355">
                  {previewExpanded ? <ChevronUp size={20} data-id="element-356" /> : <ChevronDown size={20} data-id="element-357" />}
                </button>
              </div>
              {previewExpanded && <div className="px-4 sm:px-6 py-4" data-id="element-358">
                  <div className="p-4 space-y-6" data-id="element-359">
                    {/* Preview Toggle */}
                    <div className="flex items-center justify-between" data-id="element-360">
                      <div data-id="element-361">
                        <h3 className="text-sm font-medium text-[#376770]" data-id="element-362">
                          Enable Public Preview
                        </h3>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-363">
                          Allow users to view a portion of the work
                        </p>
                      </div>
                      <button onClick={togglePreviewEnabled} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${previewSettings.enabled ? 'bg-[#376770]' : 'bg-gray-300'}`} data-id="element-364">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${previewSettings.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-365" />
                      </button>
                    </div>
                    {previewSettings.enabled && <>
                        {/* Public Link */}
                        <div className="border rounded-md p-4 bg-gray-50" data-id="element-366">
                          <div className="flex items-center justify-between" data-id="element-367">
                            <div className="flex items-center" data-id="element-368">
                              <Globe size={16} className="text-gray-500 mr-2" data-id="element-369" />
                              <span className="text-sm font-medium text-[#376770]" data-id="element-370">
                                Public Link
                              </span>
                            </div>
                            <button onClick={copyPublicLink} className="flex items-center text-xs font-medium text-[#376770] hover:text-[#164149]" data-id="element-371">
                              <Copy size={14} className="mr-1" data-id="element-372" />
                              Copy
                            </button>
                          </div>
                          <div className="mt-2" data-id="element-373">
                            <div className="text-xs text-gray-500 bg-white border rounded px-3 py-2 truncate" data-id="element-374">
                              {`${window.location.origin}/book/public/${bookId}`}
                            </div>
                          </div>
                        </div>
                        {/* Preview Percentage */}
                        <div data-id="element-375">
                          <div className="flex items-center justify-between mb-2" data-id="element-376">
                            <h3 className="text-sm font-medium text-[#376770]" data-id="element-377">
                              Visible Percentage
                            </h3>
                            <span className="text-xs font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded" data-id="element-378">
                              {previewSettings.percentage}%
                            </span>
                          </div>
                          <input type="range" min="5" max="50" step="5" value={previewSettings.percentage} onChange={handlePercentageChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" data-id="element-379" />
                          <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-380">
                            <span data-id="element-381">5%</span>
                            <span data-id="element-382">25%</span>
                            <span data-id="element-383">50%</span>
                          </div>
                        </div>
                        {/* Whitelist */}
                        <div data-id="element-384">
                          <h3 className="text-sm font-medium text-[#376770] mb-2" data-id="element-385">
                            Whitelist
                          </h3>
                          <p className="text-xs text-gray-500 mb-3" data-id="element-386">
                            Add email addresses that can access the preview
                          </p>
                          <div className="flex gap-2 mb-3" data-id="element-387">
                            <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email" className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#376770]/20 focus:border-[#376770]" data-id="element-388" />
                            <button onClick={addEmailToWhitelist} className="px-3 py-2 bg-[#376770] text-white rounded-md text-sm hover:bg-[#164149] transition-colors" data-id="element-389">
                              <UserPlus size={16} data-id="element-390" />
                            </button>
                          </div>
                          {previewSettings.whitelist.length > 0 ? <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-391">
                              {previewSettings.whitelist.map(email => <div key={email} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md" data-id="element-392">
                                  <span className="text-sm text-gray-700" data-id="element-393">
                                    {email}
                                  </span>
                                  <button onClick={() => removeEmailFromWhitelist(email)} className="text-red-500 hover:text-red-700" data-id="element-394">
                                    <EyeOff size={16} data-id="element-395" />
                                  </button>
                                </div>)}
                            </div> : <div className="text-center py-4 bg-gray-50 rounded-md" data-id="element-396">
                              <p className="text-sm text-gray-500" data-id="element-397">
                                No users in whitelist
                              </p>
                              <p className="text-xs text-gray-400 mt-1" data-id="element-398">
                                Preview will be public
                              </p>
                            </div>}
                        </div>
                        {/* Time Window */}
                        <div data-id="element-399">
                          <div className="flex items-center justify-between mb-3" data-id="element-400">
                            <div data-id="element-401">
                              <h3 className="text-sm font-medium text-[#376770]" data-id="element-402">
                                Time Window
                              </h3>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-403">
                                Set a period when the preview will be available
                              </p>
                            </div>
                            <button onClick={toggleTimeWindowEnabled} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${previewSettings.timeWindow.enabled ? 'bg-[#376770]' : 'bg-gray-300'}`} data-id="element-404">
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${previewSettings.timeWindow.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-405" />
                            </button>
                          </div>
                          {previewSettings.timeWindow.enabled && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3" data-id="element-406">
                              <div data-id="element-407">
                                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-408">
                                  Start Date
                                </label>
                                <input type="date" value={previewSettings.timeWindow.startDate} onChange={e => handleDateChange('startDate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#376770]/20 focus:border-[#376770]" data-id="element-409" />
                              </div>
                              <div data-id="element-410">
                                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-411">
                                  End Date
                                </label>
                                <input type="date" value={previewSettings.timeWindow.endDate} onChange={e => handleDateChange('endDate', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#376770]/20 focus:border-[#376770]" data-id="element-412" />
                              </div>
                            </div>}
                        </div>
                        {/* Save Button */}
                        <div className="pt-4 border-t border-gray-200" data-id="element-413">
                          <button className="w-full px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors" data-id="element-414">
                            Save Preview Settings
                          </button>
                        </div>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}
        {/* Other works by the author */}
        <div className="mt-8" data-id="element-415">
          <AuthorWorksCarousel works={books} currentBookId={book.id} authorId={book.author.id} authorName={book.author.name} data-id="element-416" />
        </div>
        <div className="mb-12" data-id="element-417"></div>
      </div>
    </div>;
}
// Helper function to get status label
function getStatusLabel(status) {
  switch (status) {
    case 'published':
      return 'Published';
    case 'unpublished':
      return 'Unpublished';
    case 'in-progress':
      return 'In Progress';
    case 'in-transfer':
      return 'In Transfer';
    default:
      return status;
  }
}
// Helper function to get status badge color
function getStatusBadgeColor(status) {
  switch (status) {
    case 'published':
      return 'bg-green-100 text-green-800';
    case 'unpublished':
      return 'bg-yellow-100 text-yellow-800';
    case 'in-progress':
      return 'bg-blue-100 text-blue-800';
    case 'in-transfer':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
function InfoCard({
  icon,
  label,
  value,
  subtext,
  progressValue
}) {
  return <div className="flex items-start p-4 rounded-lg border border-gray-100" data-id="element-418">
      <div className="mr-3 text-gray-500 mt-0.5" data-id="element-419">{icon}</div>
      <div className="flex-1" data-id="element-420">
        <div className="text-sm text-gray-500" data-id="element-421">{label}</div>
        <div className="text-base font-semibold mt-1 text-[#161c2d]" data-id="element-422">
          {value}
        </div>
        {subtext && <div className="text-sm text-gray-500 mt-1" data-id="element-423">{subtext}</div>}
        {progressValue !== undefined && <div className="w-full h-2 bg-gray-100 rounded-full mt-2.5" data-id="element-424">
            <div className="h-full bg-[#161c2d] rounded-full" style={{
          width: `${progressValue}%`
        }} data-id="element-425"></div>
          </div>}
      </div>
    </div>;
}